import { FormEvent, useEffect, useState } from "react";
import {
  ApiError,
  ModelCatalogEntry,
  ModelProvider,
  ModelSetting,
  ModelSettingUpdate,
  ModelSettingsList,
  TASK_LABELS,
  api,
} from "../lib/api";
import { useAuth } from "../lib/auth";

type DraftMap = Record<string, Draft>;

interface Draft {
  provider: ModelProvider;
  model: string;
  modelMode: "preset" | "custom";
  customModel: string;
  max_tokens: string;
  temperature: string;
}

function modelsForProvider(
  s: ModelSetting,
  catalog: ModelCatalogEntry[],
  provider: ModelProvider,
): string[] {
  // Per-slot allow list (e.g. video_vision is constrained to a specific
  // OpenAI vision model set) takes precedence over the global catalog so
  // we never offer a model the call site can't actually speak.
  const perSlot = s.allowed_models?.[provider];
  if (perSlot && perSlot.length > 0) return perSlot;
  return catalog.find((c) => c.provider === provider)?.models ?? [];
}

function providersForSlot(
  s: ModelSetting,
  fallback: ModelProvider[],
): ModelProvider[] {
  if (s.allowed_providers && s.allowed_providers.length > 0) {
    return s.allowed_providers;
  }
  return fallback;
}

function draftFromSetting(s: ModelSetting, catalog: ModelCatalogEntry[]): Draft {
  const presetList = modelsForProvider(s, catalog, s.provider);
  const isPreset = presetList.includes(s.model);
  return {
    provider: s.provider,
    model: isPreset ? s.model : (presetList[0] ?? ""),
    modelMode: isPreset ? "preset" : "custom",
    customModel: isPreset ? "" : s.model,
    max_tokens: s.max_tokens != null ? String(s.max_tokens) : "",
    temperature: s.temperature != null ? String(s.temperature) : "",
  };
}

function effectiveModel(d: Draft): string {
  return d.modelMode === "custom" ? d.customModel.trim() : d.model;
}

export default function AdminModels() {
  const user = useAuth((s) => s.user);
  const [data, setData] = useState<ModelSettingsList | null>(null);
  const [drafts, setDrafts] = useState<DraftMap>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [savedKey, setSavedKey] = useState<string | null>(null);

  const isOwner = user?.role === "owner";

  useEffect(() => {
    if (!isOwner) return;
    void load();
  }, [isOwner]);

  async function load() {
    setBusy(true);
    setErr(null);
    try {
      const resp = await api.get<ModelSettingsList>("/admin/models");
      setData(resp);
      const next: DraftMap = {};
      for (const s of resp.settings) {
        next[s.task_key] = draftFromSetting(s, resp.catalog);
      }
      setDrafts(next);
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Failed to load model settings");
    } finally {
      setBusy(false);
    }
  }

  function patchDraft(taskKey: string, patch: Partial<Draft>) {
    setDrafts((d) => ({ ...d, [taskKey]: { ...d[taskKey], ...patch } }));
    setSavedKey((k) => (k === taskKey ? null : k));
  }

  function resetDraft(taskKey: string) {
    if (!data) return;
    const s = data.settings.find((x) => x.task_key === taskKey);
    if (!s) return;
    setDrafts((d) => ({ ...d, [taskKey]: draftFromSetting(s, data.catalog) }));
    setSavedKey((k) => (k === taskKey ? null : k));
  }

  function isDirty(taskKey: string): boolean {
    if (!data) return false;
    const s = data.settings.find((x) => x.task_key === taskKey);
    const d = drafts[taskKey];
    if (!s || !d) return false;
    const liveMax = s.max_tokens != null ? String(s.max_tokens) : "";
    const liveTemp = s.temperature != null ? String(s.temperature) : "";
    return (
      d.provider !== s.provider ||
      effectiveModel(d) !== s.model ||
      d.max_tokens !== liveMax ||
      d.temperature !== liveTemp
    );
  }

  async function save(taskKey: string, ev: FormEvent) {
    ev.preventDefault();
    const d = drafts[taskKey];
    if (!d) return;

    const model = effectiveModel(d);
    if (!model) {
      setErr("Model id cannot be empty.");
      return;
    }

    const body: ModelSettingUpdate = {
      provider: d.provider,
      model,
      max_tokens: d.max_tokens.trim() === "" ? null : Number(d.max_tokens),
      temperature: d.temperature.trim() === "" ? null : Number(d.temperature),
    };
    if (body.max_tokens != null && (!Number.isFinite(body.max_tokens) || body.max_tokens < 1)) {
      setErr("Max tokens must be a positive integer (or blank to use the default).");
      return;
    }
    if (
      body.temperature != null &&
      (!Number.isFinite(body.temperature) || body.temperature < 0 || body.temperature > 2)
    ) {
      setErr("Temperature must be between 0.0 and 2.0 (or blank to use the default).");
      return;
    }

    setErr(null);
    setSavingKey(taskKey);
    try {
      const updated = await api.patch<ModelSetting>(
        `/admin/models/${encodeURIComponent(taskKey)}`,
        body,
      );
      setData((cur) => {
        if (!cur) return cur;
        return {
          ...cur,
          settings: cur.settings.map((s) => (s.task_key === taskKey ? updated : s)),
        };
      });
      setDrafts((cur) => ({
        ...cur,
        [taskKey]: draftFromSetting(updated, data?.catalog ?? []),
      }));
      setSavedKey(taskKey);
      setTimeout(() => setSavedKey((k) => (k === taskKey ? null : k)), 2500);
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.message : "Save failed");
    } finally {
      setSavingKey(null);
    }
  }

  if (!isOwner) {
    return (
      <div className="admin">
        <header className="admin__head">
          <div>
            <div className="page-eyebrow">Model settings</div>
            <h1>Model settings</h1>
          </div>
        </header>
        <div className="alert alert--error" role="alert">
          Model settings are restricted to organization owners.
        </div>
      </div>
    );
  }

  return (
    <div className="admin">
      <header className="admin__head">
        <div>
          <div className="page-eyebrow">Site settings</div>
          <h1>Models</h1>
          <p className="admin__lead">
            Pick which AI model handles each task in the application. These
            are <strong>site-wide</strong> settings — every organization
            uses the same models. Changes take effect within ~30 seconds
            (no server restart needed).
          </p>
        </div>
      </header>

      {err && <div className="alert alert--error">{err}</div>}
      {busy && !data && <div className="muted">Loading…</div>}

      <div className="admin__grid">
        {data?.settings.map((s) => {
          const d = drafts[s.task_key];
          if (!d) return null;
          const label = TASK_LABELS[s.task_key] ?? {
            title: s.task_key,
            blurb: s.description ?? "",
          };
          const presetList = modelsForProvider(s, data.catalog, d.provider);
          const providerOptions = providersForSlot(s, data.providers);
          const dirty = isDirty(s.task_key);
          const saving = savingKey === s.task_key;
          const justSaved = savedKey === s.task_key && !dirty;

          return (
            <form
              key={s.task_key}
              className={`admin-card${dirty ? " admin-card--dirty" : ""}`}
              onSubmit={(e) => save(s.task_key, e)}
            >
              <div className="admin-card__head">
                <div>
                  <div className="admin-card__task-id muted">{s.task_key}</div>
                  <h2 className="admin-card__title">{label.title}</h2>
                  <p className="admin-card__blurb">{label.blurb}</p>
                </div>
                {s.is_fallback && (
                  <span
                    className="admin-card__badge admin-card__badge--warn"
                    title="No row exists in model_settings for this slot — falling back to engine/.env defaults. Saving will create the row."
                  >
                    Using env fallback
                  </span>
                )}
              </div>

              <div className="admin-card__grid">
                <div className="admin-card__field">
                  <label htmlFor={`prov-${s.task_key}`}>Provider</label>
                  <select
                    id={`prov-${s.task_key}`}
                    value={d.provider}
                    onChange={(e) => {
                      const nextProvider = e.target.value as ModelProvider;
                      const nextModels = modelsForProvider(s, data.catalog, nextProvider);
                      patchDraft(s.task_key, {
                        provider: nextProvider,
                        model: nextModels[0] ?? "",
                        modelMode: "preset",
                        customModel: "",
                      });
                    }}
                  >
                    {providerOptions.map((p) => {
                      // Constrained slots only list providers that are actually
                      // wired up, so all options here are enabled. For the
                      // legacy global list we still grey out non-anthropic.
                      const isPerSlot = (s.allowed_providers?.length ?? 0) > 0;
                      const enabled =
                        isPerSlot ||
                        p === "anthropic" ||
                        (data.catalog.find((c) => c.provider === p)?.models.length ?? 0) >
                          0;
                      return (
                        <option key={p} value={p} disabled={!enabled}>
                          {p}
                          {!enabled ? " (not available yet)" : ""}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="admin-card__field admin-card__field--wide">
                  <label htmlFor={`model-${s.task_key}`}>Model</label>
                  {d.modelMode === "preset" ? (
                    <select
                      id={`model-${s.task_key}`}
                      value={d.model}
                      onChange={(e) => patchDraft(s.task_key, { model: e.target.value })}
                    >
                      {presetList.length === 0 && (
                        <option value="">(no preset models — use Custom)</option>
                      )}
                      {presetList.map((m) => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      id={`model-${s.task_key}`}
                      type="text"
                      value={d.customModel}
                      placeholder="exact model id, e.g. claude-opus-4-5"
                      onChange={(e) =>
                        patchDraft(s.task_key, { customModel: e.target.value })
                      }
                    />
                  )}
                  <div className="admin-card__field-toggle">
                    <button
                      type="button"
                      className="linklike"
                      onClick={() =>
                        patchDraft(s.task_key, {
                          modelMode: d.modelMode === "preset" ? "custom" : "preset",
                          customModel:
                            d.modelMode === "preset" ? d.model || "" : d.customModel,
                        })
                      }
                    >
                      {d.modelMode === "preset" ? "Use custom model id…" : "Use preset list"}
                    </button>
                  </div>
                </div>

                <div className="admin-card__field">
                  <label htmlFor={`mt-${s.task_key}`}>Max tokens</label>
                  <input
                    id={`mt-${s.task_key}`}
                    type="number"
                    min={1}
                    max={200000}
                    value={d.max_tokens}
                    placeholder="default"
                    onChange={(e) => patchDraft(s.task_key, { max_tokens: e.target.value })}
                  />
                </div>

                <div className="admin-card__field">
                  <label htmlFor={`temp-${s.task_key}`}>Temperature</label>
                  <input
                    id={`temp-${s.task_key}`}
                    type="number"
                    step={0.1}
                    min={0}
                    max={2}
                    value={d.temperature}
                    placeholder="default"
                    onChange={(e) => patchDraft(s.task_key, { temperature: e.target.value })}
                  />
                </div>
              </div>

              <div className="admin-card__foot">
                <div className="admin-card__meta muted">
                  {s.updated_at ? (
                    <>
                      Last updated {formatTime(s.updated_at)}
                      {s.updated_by_name ? <> by <strong>{s.updated_by_name}</strong></> : null}
                    </>
                  ) : (
                    <>Never modified — using seeded defaults.</>
                  )}
                </div>
                <div className="admin-card__actions">
                  {justSaved && <span className="admin-card__saved">Saved ✓</span>}
                  <button
                    type="button"
                    className="btn"
                    disabled={!dirty || saving}
                    onClick={() => resetDraft(s.task_key)}
                  >
                    Revert
                  </button>
                  <button
                    type="submit"
                    className="btn btn--primary"
                    disabled={!dirty || saving}
                  >
                    {saving ? "Saving…" : "Save changes"}
                  </button>
                </div>
              </div>
            </form>
          );
        })}
      </div>
    </div>
  );
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}
